// Insurance Claims Type Definitions

// Core enums
export enum InsuranceClaimStatus {
    DRAFT = "DRAFT",
    SUBMITTED = "SUBMITTED", 
    APPROVED = "APPROVED",
    DENIED = "DENIED",
    PARTIALLY_APPROVED = "PARTIALLY_APPROVED",
    PAID = "PAID"
}

// Mock data structures (to be created)
export interface Insurance {
    id: number;
    uuid: string;
    name: string;
    coveragePercentage: number;
    isActive: boolean;
}

export interface Bill {
    id: number;
    uuid: string;
    totalAmount: number;
    patientName: string;
    investigationId: number;
}

// Display interface for insurance claims
export interface InsuranceClaim {
    id: number;
    uuid: string;
    organizationName: string;
    souscripteur?: string;
    coveragePercentage: number;
    requestedAmount: number;
    approvedAmount?: number;
    guaranteeLetterNumber: string;
    guaranteeLetterIssueDate: Date;
    interventionDate?: Date;
    amountInLetters: string;
    guaranteeLetterPdfPath?: string;
    approvalLetterImagePath?: string;
    status: InsuranceClaimStatus;
    claimReference?: string;
    organizationId: number;
    notes?: string;
    billId: number;
    baseAmount?: number;
    insuranceBillUuid?: string;
    createdAt: Date;
    updatedAt: Date;
}

// Component Props
export interface InsuranceClaimFormProps {
    uuidInvestigation: string;
    uuidBill: string;
    mode: 'create' | 'edit' | 'view';
    initialData?: any;
    availableInsurances: Insurance[];
    onSuccess?: (claim: InsuranceClaim) => void;
    onCancel?: () => void;
    isReadOnly?: boolean;
    currency: string;
    languageCode: string;
}

// API Response interfaces
export interface CreateClaimResponse {
    success: boolean;
    insuranceClaim: InsuranceClaim;
    message?: string;
}

export interface UpdateClaimResponse {
    success: boolean;
    insuranceClaim: InsuranceClaim;
    message?: string;
}

export interface GetClaimsResponse {
    success: boolean;
    insuranceClaims: InsuranceClaim[];
    message?: string;
}

export interface DeleteClaimResponse {
    success: boolean;
    message?: string;
}

// Form Data interface for redux-form
export interface InsuranceClaimFormData {
    organizationName: string;  // Used internally by form - stores organization ID as string
    souscripteur?: string;
    coveragePercentage?: number;
    requestedAmount?: number;
    approvedAmount?: number;
    guaranteeLetterNumber?: string;
    guaranteeLetterIssueDate?: Date;
    interventionDate?: Date;
    amountInLetters?: string;
    guaranteeLetterPdf?: string; // base64 string
    approvalLetterImage?: string; // base64 string
    insuranceId?: number;
    status: InsuranceClaimStatus;
    claimReference?: string;
    notes?: string;
}

// Payload sent to API (excludes organizationName, includes organizationId)
export type InsuranceClaimApiPayload = Omit<InsuranceClaimFormData, 'organizationName'> & {
    organizationId?: number;
};

// Field validation result
export interface ValidationResult {
    valid: boolean;
    error?: string;
}

// API Error class
export class ApiError extends Error {
    public statusCode: number;
    public details?: any;

    constructor({ message, statusCode, details }: { message: string; statusCode: number; details?: any }) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.details = details;
    }
}

// Add a generic API result type for status handling
export interface ApiResult<T> {
    data: T;
    status: number;
} 